import {
  createAsyncThunk,
  createSlice,
  AnyAction,
  AsyncThunk,
} from "@reduxjs/toolkit";
import { WritableDraft } from "immer/dist/internal";
import moment from "moment";
import {
  Iteration,
  ListFeedbackResponseJson,
  Survey,
  SurveyAnswer,
  SurveyQuestion,
  SurveyQuestionHistory,
} from "typings/api/types";
import { SurveyRequest } from "typings/app/types";
import { del, get, post, put } from "util/api";

type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;
type PendingAction = ReturnType<GenericAsyncThunk["pending"]>;
type RejectedAction = ReturnType<GenericAsyncThunk["rejected"]>;

export interface SurveyState {
  surveys: { [key: string]: Survey };
  selected?: Survey | null;
  questions: { [key: string]: SurveyQuestion[] };
  iterations: { [key: string]: { [key: number]: Iteration } };
  feedback?: ListFeedbackResponseJson | null;
  status: string;
  error: string | null;
}

const initialState: SurveyState = {
  surveys: {},
  questions: {},
  iterations: {},
  status: "idle",
  error: null,
};

export const listSurveys = createAsyncThunk("surveys/listSurveys", async () =>
  get("survey")
);

export const getSurveyById = createAsyncThunk(
  "surveys/getSurveyById",
  async (surveyId: string) => get("survey/" + surveyId)
);

export const getSurveyFeedback = createAsyncThunk(
  "surveys/getSurveyFeedback",
  async (surveyId: string) => get(`survey/${surveyId}/feedback`)
);

export const submitSurvey = createAsyncThunk(
  "surveys/submitSurvey",
  async (body: SurveyRequest) => {
    const data = await post("survey", body);
    return data;
  }
);

export const deleteSurvey = createAsyncThunk(
  "surveys/deleteSurvey",
  async (surveyId: string) => {
    await del(`survey/${surveyId}`);
    return surveyId;
  }
);

export const updateParticipants = createAsyncThunk(
  "surveys/updateParticipants",
  async ({
    surveyId,
    participants,
  }: {
    surveyId: string;
    participants: string[];
  }) => {
    return put(`survey/${surveyId}`, { participants });
  }
);

export const endSurvey = createAsyncThunk(
  "surveys/endSurvey",
  async (surveyId: string) => {
    return put(`survey/${surveyId}`, { endDate: moment().format() });
  }
);

export const submitAnswer = createAsyncThunk(
  "surveys/submitAnswer",
  async ({
    surveyId,
    answers,
  }: {
    surveyId: string;
    answers: SurveyAnswer;
  }) => {
    return post(
      `survey/${surveyId}/feedback?token=${1234}`,
      Object.values(answers)
    );
  }
);

function isPendingAction(action: AnyAction): action is PendingAction {
  return action.type.endsWith("/pending");
}
function isRejectedAction(action: AnyAction): action is RejectedAction {
  return action.type.endsWith("/rejected");
}

function updateItem(
  state: WritableDraft<SurveyState>,
  action: { payload: { id: string; updates: Survey } }
) {
  state.surveys[action.payload.id] = action.payload.updates;
}

export const surveySlice = createSlice({
  name: "survey",
  initialState,
  reducers: {
    updateSurvey: updateItem,
  },
  extraReducers: (builder) => {
    builder.addCase(listSurveys.fulfilled, (state, { payload }) => {
      state.status = "succeeded";
      Object.assign(
        state.surveys,
        ...(payload as Survey[]).map((survey) => ({
          [survey.surveyId]: survey,
        }))
      );
    });
    builder.addCase(getSurveyById.fulfilled, (state, { payload }) => {
      state.status = "succeeded";
      const { survey, iterations, questions } = payload;
      state.surveys[survey.surveyId] = survey;
      state.questions[survey.surveyId] = questions;
      state.iterations[survey.surveyId] = iterations;
    });
    builder.addCase(getSurveyFeedback.fulfilled, (state, { payload }) => {
      state.status = "succeeded";
      state.feedback = payload;
    });
    builder.addCase(updateParticipants.fulfilled, (state, { payload }) => {
      const survey = payload as Survey;
      updateItem(state, {
        payload: { id: survey.surveyId, updates: survey },
      });
    });
    builder.addCase(endSurvey.fulfilled, (state, { payload }) => {
      const survey = payload as Survey;
      updateItem(state, {
        payload: { id: survey.surveyId, updates: survey },
      });
    });
    builder.addCase(submitAnswer.fulfilled, (state) => {
      state.status = "succeeded";
    });
    builder.addCase(deleteSurvey.fulfilled, (state, { payload }) => {
      state.status = "succeeded";
      delete state.surveys[payload];
    });
    builder
      .addCase(submitSurvey.fulfilled, (state, { payload }) => {
        state.status = "succeeded";
        state.surveys[payload.surveyId] = payload;
      })
      .addMatcher(isPendingAction, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.status = "failed";
        state.error = (action.error as Error).message;
      });
  },
});

// Action creators are generated for each case reducer function
export const { updateSurvey } = surveySlice.actions;

export const selectSurveyList = ({
  survey: { surveys },
}: {
  survey: SurveyState;
}) => Object.values(surveys);

export const selectSurveyById =
  (surveyId: string) =>
  ({ survey: { surveys } }: { survey: SurveyState }) =>
    surveys[surveyId];

export const selectIterationsForSurvey =
  (surveyId: string) =>
  ({ survey }: { survey: SurveyState }) =>
    survey.iterations[surveyId] || {};

export const selectQuestionsForSurvey =
  (surveyId: string) =>
  ({ survey }: { survey: SurveyState }) =>
    survey.questions[surveyId];

export const selectFeedbackForIteration =
  (iteration?: number) =>
  ({ survey: { feedback } }: { survey: SurveyState }) => {
    if (iteration === undefined) return feedback;
    return (feedback || []).map(({ feedback: fb, ...rest }) => ({
      ...rest,
      feedback: {
        results: fb?.results,
        answers: fb?.answers?.filter(
          ({ answerId }) => answerId?.split("#")[1] === iteration.toString()
        ),
      },
    }));
  };

export const selectHistoryForSurvey =
  (surveyId: string) =>
  ({ survey }: { survey: SurveyState }) => {
    const selectedSurvey = survey.surveys[surveyId];
    const historyOverview: { [key: string]: SurveyQuestionHistory } = {};
    selectedSurvey.questions.forEach(({ questionId, history }) => {
      if (questionId && history) historyOverview[questionId] = history;
    });
  };

export default surveySlice.reducer;
